import { useUpdatePassword } from '@application/api/account/useUpdatePassword';
import { useModal, useRouter, useShowToast, useValidation } from '@application/utils';
import { ChangePasswordModal } from '@presentation/components';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { object, ref, string } from 'yup';

const init: ReturnType<typeof useChangePasswordModalSingleton> = {
  show: () => undefined,
  showFromClick: () => undefined,
  close: () => undefined,
  modal: {
    onCloseClick: undefined,
    open: false,
    position: [0, 0],
  },
};

export const useChangePasswordModalSingleton = () => {
  const modalHook = useModal({});
  return modalHook;
};

export const useChangePasswordModal = singletonHook(init, useChangePasswordModalSingleton);

export const ChangePasswordModalController: FC = () => {
  const { modal, close } = useChangePasswordModal();
  const { updatePassword } = useUpdatePassword();
  const { requiredMessage, minCharactersMessage, passwordDifferentMessage } = useValidation();
  const { translate } = useRouter();
  const { showToast } = useShowToast();

  const validationSchema = useMemo(() => {
    return object({
      password: string().min(6, minCharactersMessage(6)).required(requiredMessage),
      confirmPassword: string()
        .oneOf([ref('password'), null], passwordDifferentMessage)
        .required(requiredMessage),
    });
  }, [minCharactersMessage, passwordDifferentMessage, requiredMessage]);

  const showSuccessToast = useCallback(() => {
    showToast(translate('common.toasts.changePasswordSuccess'), { type: 'success' });
  }, [showToast, translate]);

  return (
    <ChangePasswordModal
      modal={modal}
      form={{
        initialValues: {
          password: '',
          confirmPassword: '',
        },
        validationSchema,
        onSubmit: async ({ password }) => {
          await updatePassword(password);
          close();
          showSuccessToast();
        },
      }}
    />
  );
};
